<template>
    <div class="flex flex-col gap-3">
        <!-- Parent Nested Select -->
        <div class="flex flex-col gap-1">
            <label class="font-semibold" :for="name">{{ label }}<span v-if="required" class="text-red-600">*</span></label>
            <Multiselect 
            ref="dropdown"
                v-model="parentInputValue" 
                :options="parentOptions" 
                :multiple="false" 
                @select="fetchChildren"
                @clear="clearChildren"
            />
            <p v-show="parentErrorMessage || parentMeta.valid">
                {{ parentErrorMessage }}
            </p>
        </div>

        <!-- Child Multiselect with Tags -->
        <div v-if="childrenOptions.length > 0" class="flex flex-col gap-1">
            <label class="font-semibold" :for="'child'">{{ childLabel }}</label>
            <Multiselect 
                v-model="selectedChildren" 
                :options="childrenOptions" 
                mode="tags" 
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted  } from 'vue';
import { useI18n } from "vue-i18n";
const { t } = useI18n();
import { useField } from 'vee-validate';
import * as yup from 'yup';
import Multiselect from '@vueform/multiselect';
import api from '@/api';
import { appendSpinner, removeSpinner } from "@/helpers"
const props = defineProps({
    value: {
        type: String,
        default: undefined,
    },
    label: {
        type: String,
        required: true,
    },
    name: {
        type: String,
        required: true,
    },
    required: {
        type: Boolean,
        default: true,
    },
    options: {
        type: Array,
        required: true,
    },
    property: {
        type: Number,
        default: undefined,
    },
    defaultMultiValues: {
        type: Array,
        default: undefined
    }
});

// Validation rules
let rules = yup.string().required(t('warning.REQUIRED_MESSAGE'));
const { meta: parentMeta, value: parentInputValue, errorMessage: parentErrorMessage, validate: parentValidate } = useField(props.name, rules, {
    initialValue: props.value,
});

const isLoading = ref(false)
const hasNext_ = ref(false)
const page_ = ref(1)
const dropdown = ref();
let parentOptions = ref([]);
// Reactive child options and selections
let childrenOptions = ref([]);
let selectedChildren = ref([]);
let childLabel = ref('');



// Fetch child options when a parent is selected
const fetchChildren = () => {
    const selectedParent = parentOptions.value?.find(option => option.value === parentInputValue.value);
    if (selectedParent) {
        childrenOptions.value = selectedParent.children.map(child => ({
            value: child.id,
            label: child.name,
        }));
        childLabel.value = selectedParent.label; // Dynamic child label
        selectedChildren.value = []; // Reset child selections
    } else {
        clearChildren();
    }
};

// Clear child options and selections
const clearChildren = () => {
    childrenOptions.value = [];
    selectedChildren.value = [];
    childLabel.value = '';
};

// Emit the selected child values


// Expose validation method
const validateField = async () => {
    const parentValidatedData = await parentValidate();
    if (parentInputValue.value && selectedChildren.value) {
        return { value: [...Object.values(parentInputValue.value), ...Object.values(selectedChildren.value)], valid: parentValidatedData.valid, mode: 'multi', property: props.property };
    }
    return { value: undefined, valid: false, mode: 'multi' };
};
watch(isLoading, (newVal) => {
    const dropdown0 = dropdown.value;
    const list = dropdown0.$el.querySelector('.multiselect-options');
    if (newVal) {
        appendSpinner(list);

    } else {
        removeSpinner(list);

    }
});
const fetchMoreOptions = async () => {
    isLoading.value = true;
    try {
        const response = await api.get(
            `attributes/select_values/?property=${props.property}&include_children=true&page=${page_.value}`
        );
        const { results, next } = response.data;
        hasNext_.value = !!next;
        let data = results?.map(item => ({
            value: item.id,
            label: item.name,
            children: item.children,
        }))
        parentOptions.value = [...parentOptions.value, ...data];
    } catch (error) {
        console.error("Error fetching more options:", error);
    } finally {
        isLoading.value = false;
    }
}
const determineIfEndOfScroll = async (list) => {
    if (list.clientHeight !== list.scrollHeight && list.scrollTop + list.clientHeight >= list.scrollHeight) {
        if (!isLoading.value && hasNext_.value) {
            const page = page_.value + 1;
            page_.value = page;
            await fetchMoreOptions(page_.value)
           
        }
    }
}
defineExpose({
    validateField,
});

onMounted(() => {
    fetchMoreOptions()
    const dropdown0 = dropdown.value;
    const list = dropdown0?.$el?.querySelector('.multiselect-dropdown');
    list.addEventListener('scroll', () => determineIfEndOfScroll(list));
});
onUnmounted(() => {
    const dropdown0 = dropdown.value;
    if (dropdown0){
        const list = dropdown0?.$el?.querySelector('.multiselect-dropdown');
        list.removeEventListener('scroll', () => determineIfEndOfScroll(list));
    }
});
</script>

<style>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
